import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Moon, HeartPulse, Activity, Gauge, Footprints, Scale, Wind, LineChart as LineIcon, LayoutDashboard, AlertTriangle,
} from "lucide-react";

/**
 * Faixas clínicas de referência (adulto). status: ok | warn | alert
 * Retorna null quando não há valor.
 */
function classify(key, v) {
  if (v == null || v === "") return null;
  const n = parseFloat(v);
  if (Number.isNaN(n)) return null;
  switch (key) {
    case "resting_hr":
      if (n < 50 || n > 100) return "alert";
      if (n > 90) return "warn";
      return "ok";
    case "bp_sys":
      if (n >= 140 || n < 90) return "alert";
      if (n >= 130) return "warn";
      return "ok";
    case "bp_dia":
      if (n >= 90 || n < 60) return "alert";
      if (n >= 85) return "warn";
      return "ok";
    case "spo2":
      if (n < 92) return "alert";
      if (n < 95) return "warn";
      return "ok";
    case "sleep_hours":
      if (n < 6) return "alert";
      if (n < 7) return "warn";
      return "ok";
    case "hrv":
      if (n < 20) return "alert";
      if (n < 30) return "warn";
      return "ok";
    case "steps":
      if (n < 3000) return "alert";
      if (n < 5000) return "warn";
      return "ok";
    default:
      return "ok"; // peso: neutro
  }
}

const STATUS_TEXT = {
  ok: "text-emerald-600",
  warn: "text-amber-500",
  alert: "text-red-600",
};
const STATUS_CARD = {
  ok: "border-emerald-200 bg-emerald-50/40",
  warn: "border-amber-200 bg-amber-50/50",
  alert: "border-red-200 bg-red-50/60",
};
const STATUS_LABEL = { ok: "Normal", warn: "Atenção", alert: "Fora do normal" };

const CARDS = [
  { key: "resting_hr", label: "FC repouso", unit: "bpm", icon: HeartPulse, ref: "60–100 bpm" },
  { key: "bp", label: "Pressão arterial", unit: "mmHg", icon: Gauge, ref: "< 130/85 mmHg" },
  { key: "spo2", label: "SpO2", unit: "%", icon: Wind, ref: "≥ 95%" },
  { key: "sleep_hours", label: "Sono", unit: "h", icon: Moon, ref: "≥ 7h" },
  { key: "hrv", label: "HRV", unit: "ms", icon: Activity, ref: "≥ 30 ms" },
  { key: "steps", label: "Passos", unit: "", icon: Footprints, ref: "≥ 5.000" },
  { key: "weight", label: "Peso", unit: "kg", icon: Scale, ref: "—" },
];

const CHART_METRICS = [
  { key: "resting_hr", label: "FC repouso", unit: "bpm", color: "hsl(var(--destructive))" },
  { key: "bp_sys", label: "PA sistólica", unit: "mmHg", color: "hsl(var(--warning))" },
  { key: "spo2", label: "SpO2", unit: "%", color: "hsl(var(--chart-3))" },
  { key: "sleep_hours", label: "Sono", unit: "h", color: "hsl(var(--chart-5))" },
  { key: "hrv", label: "HRV", unit: "ms", color: "hsl(var(--accent-ai))" },
  { key: "weight", label: "Peso", unit: "kg", color: "hsl(var(--primary))" },
];

export default function AcompanhamentoTab({ patientId }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    api.get(`/patients/${patientId}/wearables`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [patientId]);

  // ordenado do mais recente para o mais antigo (tabela)
  const sorted = useMemo(
    () => [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")),
    [rows]
  );
  const latest = sorted[0] || {};

  // séries dos últimos 30 dias (gráficos)
  const seriesByMetric = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    const cutStr = cutoff.toISOString().slice(0, 10);
    const recent = rows.filter((r) => (r.date || "") >= cutStr);
    const out = {};
    for (const mt of CHART_METRICS) {
      const pts = recent
        .filter((r) => r[mt.key] != null)
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
        .map((r) => ({ date: r.date, valor: r[mt.key] }));
      if (pts.length) out[mt.key] = pts;
    }
    return out;
  }, [rows]);

  // alertas ativos (com base no registo mais recente)
  const activeAlerts = useMemo(() => {
    const out = [];
    for (const c of CARDS) {
      if (c.key === "bp") {
        const s = classify("bp_sys", latest.bp_sys);
        const d = classify("bp_dia", latest.bp_dia);
        const st = [s, d].includes("alert") ? "alert" : [s, d].includes("warn") ? "warn" : null;
        if (st && st !== "ok") out.push({ label: c.label, value: `${latest.bp_sys}/${latest.bp_dia} mmHg`, status: st });
      } else {
        const st = classify(c.key, latest[c.key]);
        if (st && st !== "ok") out.push({ label: c.label, value: `${latest[c.key]} ${c.unit}`, status: st });
      }
    }
    return out;
  }, [latest]);

  const fmt = (v) => (v == null || v === "" ? "—" : v);
  const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 };

  const cellClass = (key, v) => {
    const st = classify(key, v);
    return st ? `text-right font-mono-data font-semibold ${STATUS_TEXT[st]}` : "text-right font-mono-data";
  };

  return (
    <div className="space-y-8" data-testid="acompanhamento-tab">
      <div>
        <h3 className="font-heading text-xl font-medium flex items-center gap-2">
          <LayoutDashboard className="w-5 h-5 text-ai" /> Dashboard de acompanhamento
        </h3>
        <p className="text-sm text-muted-foreground">
          Sinais vitais mais recentes, evolução e alertas visuais quando um valor está fora do normal.
        </p>
      </div>

      {rows.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          Ainda sem dados. Registe sinais vitais na aba <strong>Wearables</strong> para ver o acompanhamento.
        </Card>
      ) : (
        <>
          {/* Faixa de alertas ativos */}
          {activeAlerts.length > 0 && (
            <Card className="p-4 border-red-200 bg-red-50/50" data-testid="acompanhamento-alerts">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-700">
                    {activeAlerts.length} indicador(es) fora do normal no último registo ({latest.date})
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {activeAlerts.map((a) => (
                      <span key={a.label}
                        className={`text-xs px-2 py-1 rounded-full border ${a.status === "alert" ? "border-red-300 bg-red-100 text-red-700" : "border-amber-300 bg-amber-100 text-amber-700"}`}>
                        {a.label}: {a.value}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* Cartões dos últimos valores */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="acompanhamento-cards">
            {CARDS.map((c) => {
              const Icon = c.icon;
              let value, st;
              if (c.key === "bp") {
                value = latest.bp_sys && latest.bp_dia ? `${latest.bp_sys}/${latest.bp_dia}` : "—";
                const s = classify("bp_sys", latest.bp_sys);
                const d = classify("bp_dia", latest.bp_dia);
                st = [s, d].includes("alert") ? "alert" : [s, d].includes("warn") ? "warn" : (s || d) ? "ok" : null;
              } else {
                value = latest[c.key] ?? "—";
                st = classify(c.key, latest[c.key]);
              }
              return (
                <Card key={c.key}
                  className={`p-5 shadow-none border ${st ? STATUS_CARD[st] : "border-border"}`}
                  data-testid={`acompanhamento-card-${c.key}`}>
                  <div className="flex items-center justify-between mb-3">
                    <Icon className={`w-5 h-5 ${st ? STATUS_TEXT[st] : "text-muted-foreground"}`} />
                    {st && (
                      <span className={`text-[10px] font-medium ${STATUS_TEXT[st]}`}>{STATUS_LABEL[st]}</span>
                    )}
                  </div>
                  <div className={`font-mono-data text-2xl font-semibold ${st ? STATUS_TEXT[st] : ""}`}>
                    {value} <span className="text-sm font-normal text-muted-foreground">{value !== "—" ? c.unit : ""}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{c.label}</div>
                  <div className="text-[10px] text-muted-foreground/70 mt-0.5">Normal: {c.ref}</div>
                </Card>
              );
            })}
          </div>

          {/* Gráficos de evolução (30 dias) */}
          <div>
            <h4 className="font-heading text-lg font-medium flex items-center gap-2 mb-4">
              <LineIcon className="w-4 h-4 text-ai" /> Evolução (últimos 30 dias)
            </h4>
            {Object.keys(seriesByMetric).length === 0 ? (
              <Card className="p-10 text-center text-muted-foreground border-dashed">
                Sem dados nos últimos 30 dias.
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="acompanhamento-charts">
                {CHART_METRICS.filter((mt) => seriesByMetric[mt.key]).map((mt) => {
                  const pts = seriesByMetric[mt.key];
                  const last = pts[pts.length - 1];
                  const lastSt = classify(mt.key, last.valor);
                  return (
                    <Card key={mt.key} className="p-4 border-border shadow-none" data-testid={`acompanhamento-chart-${mt.key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h5 className="text-sm font-medium">{mt.label}</h5>
                        <span className={`font-mono-data text-sm font-semibold ${lastSt ? STATUS_TEXT[lastSt] : ""}`}
                          style={lastSt ? undefined : { color: mt.color }}>
                          {last.valor} {mt.unit}
                        </span>
                      </div>
                      {pts.length === 1 ? (
                        <div className="h-[150px] flex flex-col items-center justify-center text-center">
                          <span className={`font-mono-data text-3xl font-semibold ${lastSt ? STATUS_TEXT[lastSt] : ""}`}
                            style={lastSt ? undefined : { color: mt.color }}>
                            {last.valor}<span className="text-base font-normal text-muted-foreground"> {mt.unit}</span>
                          </span>
                          <span className="text-xs text-muted-foreground mt-1">apenas 1 medição ({last.date})</span>
                        </div>
                      ) : (
                        <ResponsiveContainer width="100%" height={150}>
                          <LineChart data={pts} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                            <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                            <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} width={32} />
                            <Tooltip contentStyle={tip} formatter={(v) => [`${v} ${mt.unit}`, mt.label]} />
                            <Line type="monotone" dataKey="valor" name={mt.label} stroke={mt.color} strokeWidth={2} dot={{ r: 3 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tabela com alertas por cor */}
          <div>
            <h4 className="font-heading text-lg font-medium mb-4">Registos</h4>
            <Card className="p-0 border-border shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Fonte</TableHead>
                      <TableHead className="text-right">FC (bpm)</TableHead>
                      <TableHead className="text-right">PA (mmHg)</TableHead>
                      <TableHead className="text-right">SpO2 (%)</TableHead>
                      <TableHead className="text-right">Sono (h)</TableHead>
                      <TableHead className="text-right">HRV (ms)</TableHead>
                      <TableHead className="text-right">Passos</TableHead>
                      <TableHead className="text-right">Peso (kg)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sorted.map((r) => {
                      const bpSt = [classify("bp_sys", r.bp_sys), classify("bp_dia", r.bp_dia)].includes("alert")
                        ? "alert"
                        : [classify("bp_sys", r.bp_sys), classify("bp_dia", r.bp_dia)].includes("warn")
                        ? "warn" : null;
                      return (
                        <TableRow key={r.id} data-testid={`acompanhamento-row-${r.id}`}>
                          <TableCell className="font-medium">{r.date}</TableCell>
                          <TableCell className="text-muted-foreground">{r.source}</TableCell>
                          <TableCell className={cellClass("resting_hr", r.resting_hr)}>{fmt(r.resting_hr)}</TableCell>
                          <TableCell className={bpSt ? `text-right font-mono-data font-semibold ${STATUS_TEXT[bpSt]}` : "text-right font-mono-data"}>
                            {r.bp_sys && r.bp_dia ? `${r.bp_sys}/${r.bp_dia}` : "—"}
                          </TableCell>
                          <TableCell className={cellClass("spo2", r.spo2)}>{fmt(r.spo2)}</TableCell>
                          <TableCell className={cellClass("sleep_hours", r.sleep_hours)}>{fmt(r.sleep_hours)}</TableCell>
                          <TableCell className={cellClass("hrv", r.hrv)}>{fmt(r.hrv)}</TableCell>
                          <TableCell className={cellClass("steps", r.steps)}>{fmt(r.steps)}</TableCell>
                          <TableCell className="text-right font-mono-data">{fmt(r.weight)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
